
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LaunchIcon from '@mui/icons-material/Launch';
import LinkIcon from '@mui/icons-material/Link';
import { Box, Button, ClickAwayListener, Divider, IconButton, Paper, Popper, Stack, TextField, Tooltip, Typography } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import React, { useCallback, useState } from 'react';
import useConnectedApps from '../../../hooks/useConnectedApps';
import theme from '../../../theme';
import { ConnectedApp } from '../../../types/files';
import DynamicAppIcon from '../../atoms/DynamicAppIcon';

const useStyles = makeStyles(() => ({
    manageButton: {
        textTransform: 'none',
        fontFamily: 'Inter',
        fontWeight: 600,
        fontSize: '0.85rem',
        color: '#334155',
        borderRadius: 8,
        padding: '4px 8px 4px 12px',
        minWidth: 'unset',
        '&:hover': {
            background: theme.colors.neutral['100'],
        },
    },
    popperPaper: {
        width: 480,
        borderRadius: 16,
        boxShadow: '0px 12px 40px -8px rgba(16, 24, 40, 0.14), 0px 4px 12px -2px rgba(16, 24, 40, 0.06)',
        border: `1px solid ${theme.colors.neutral['200']}`,
        padding: '16px 20px',
        marginTop: 8,
    },
    appRow: {
        padding: '10px 8px',
        borderRadius: 10,
        transition: 'background 0.15s',
        '&:hover': {
            background: theme.colors.neutral['50'],
        },
    },
    appName: {
        fontFamily: 'Inter',
        fontWeight: 600,
        fontSize: '0.9rem',
        color: '#1e293b',
        lineHeight: 1.3,
    },
    appLink: {
        fontFamily: 'Inter',
        fontSize: '0.78rem',
        fontWeight: 400,
        color: theme.colors.neutral['500'],
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 240,
        lineHeight: 1.3,
    },
}));

/** Ensure a URL has a protocol so window.open doesn't treat it as a relative path. */
const ensureProtocol = (url: string): string => {
    const trimmed = url.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return `https://${trimmed}`;
};


const ConnectedApps: React.FC<{}> = () => {
    const classes = useStyles();
    const { getDisplayName, effectiveApps, updateAppLinkByName } = useConnectedApps();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [editValue, setEditValue] = useState<string>('');

    const handleToggle = useCallback((e: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(e.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, []);

    const startEditing = useCallback((app: ConnectedApp, index: number) => {
        setEditingIndex(index);
        setEditValue(app.link || '');
    }, []);

    const cancelEditing = useCallback(() => {
        setEditingIndex(null);
        setEditValue('');
    }, []);

    const saveEditing = useCallback(async () => {
        if (editingIndex === null) return;
        const app = effectiveApps[editingIndex];
        if (!app?.name) return;
        await updateAppLinkByName(app.name, editValue.trim());
        setEditingIndex(null);
        setEditValue('');
    }, [editingIndex, editValue, effectiveApps, updateAppLinkByName]);

    const open = !!anchorEl;

    return (<>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
            <Typography fontFamily="Inter" fontSize="0.85rem" fontWeight={600} color="#64748b"
                sx={{ whiteSpace: 'nowrap' }}>
                {'Connected Apps:'}
            </Typography>
            <Stack direction="row" spacing={1.25} alignItems="center">
                {effectiveApps.map((app, i) => (
                    <Tooltip key={app.link || i} title={getDisplayName(app)}>
                        <Stack
                            sx={{ cursor: app.link ? 'pointer' : 'default' }}
                            onClick={() => {
                                if (app.link) window.open(ensureProtocol(app.link), '_blank', 'noopener,noreferrer');
                            }}
                        >
                            <DynamicAppIcon link={app.link} name={app.name} size={32} grayscale={!app.link} />
                        </Stack>
                    </Tooltip>
                ))}
            </Stack>
            <Button className={classes.manageButton} onClick={handleToggle} disableRipple
                endIcon={
                    <KeyboardArrowDownIcon sx={{
                        width: 18, height: 18, color: '#64748b',
                        transition: 'transform 0.2s',
                        transform: open ? 'rotate(180deg)' : 'none',
                    }} />
                }>
                {'Manage'}
            </Button>
        </Stack>
        <Popper open={open} anchorEl={anchorEl} placement="bottom-end" sx={{ zIndex: 1500 }}>
            <ClickAwayListener onClickAway={handleClose}>
                <Paper className={classes.popperPaper}>
                    <Typography fontFamily="Inter" fontWeight={700} fontSize="1.05rem" color="#1e293b" mb={0.25}>
                        {'Manage Connected Apps'}
                    </Typography>
                    <Typography fontFamily="Inter" fontSize="0.82rem" color={theme.colors.neutral['500']} mb={1.5}>
                        {'Quick links to data sources for this company'}
                    </Typography>
                    <Divider sx={{ mb: 1.5 }} />
                    <Stack spacing={0.5}>
                        {effectiveApps.map((app, i) => {
                            const isEditing = editingIndex === i;

                            if (isEditing) {
                                return (
                                    <Stack key={app.link || i} direction="row" alignItems="center" spacing={1.5}
                                        className={classes.appRow} sx={{ padding: '8px' }}>
                                        <DynamicAppIcon link={app.link} name={app.name} size={38} grayscale={!app.link} />
                                        <TextField
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            placeholder="https://..."
                                            fullWidth
                                            autoFocus
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveEditing();
                                                if (e.key === 'Escape') cancelEditing();
                                            }}
                                            InputProps={{
                                                sx: { borderRadius: 2, fontFamily: 'Inter', fontSize: '0.85rem', height: 38 },
                                            }}
                                            size="small"
                                        />
                                        <Button variant="contained"
                                            onClick={() => saveEditing()}
                                            disabled={!editValue.trim()}
                                            sx={{
                                                height: 30, minWidth: 52, px: 1.5,
                                                borderRadius: 2, textTransform: 'none', fontWeight: 600,
                                                fontFamily: 'Inter', fontSize: '0.75rem',
                                                bgcolor: theme.colors.primary['600'],
                                                '&:hover': { bgcolor: theme.colors.primary['500'] },
                                            }}>
                                            {'Save'}
                                        </Button>
                                        <Button
                                            onClick={cancelEditing}
                                            sx={{
                                                height: 34, minWidth: 60, px: 2,
                                                textTransform: 'none', fontFamily: 'Inter', fontSize: '0.8rem',
                                                color: theme.colors.neutral['500'],
                                            }}>
                                            {'Cancel'}
                                        </Button>
                                    </Stack>
                                );
                            }

                            return (
                                <Stack key={app.link || i} direction="row" alignItems="center" justifyContent="space-between"
                                    className={classes.appRow}>
                                    <Stack direction="row" spacing={1.5} alignItems="center" flex={1} minWidth={0}>
                                        <DynamicAppIcon link={app.link} name={app.name} size={38} grayscale={!app.link} />
                                        <Stack minWidth={0}>
                                            <Typography className={classes.appName}>{getDisplayName(app)}</Typography>
                                            <Typography className={classes.appLink}>
                                                {app.link || 'No link set'}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                    <Stack direction="row" spacing={0.25} alignItems="center">
                                        {!!app.link && (
                                            <Tooltip title={getDisplayName(app)}>
                                                <IconButton size="small"
                                                    onClick={() => window.open(ensureProtocol(app.link!), '_blank', 'noopener,noreferrer')}
                                                    sx={{ '&:hover': { background: theme.colors.neutral['100'] } }}>
                                                    <LaunchIcon sx={{ width: 18, height: 18, color: theme.colors.neutral['400'] }} />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <IconButton size="small"
                                            onClick={() => startEditing(app, i)}
                                            sx={{ '&:hover': { background: theme.colors.neutral['100'] } }}>
                                            <EditOutlinedIcon sx={{ width: 18, height: 18, color: theme.colors.neutral['400'] }} />
                                        </IconButton>

                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Stack>
                    {!effectiveApps.length && (
                        <Box py={3} textAlign="center">
                            <LinkIcon sx={{ width: 36, height: 36, color: theme.colors.neutral['300'] }} />
                            <Typography fontFamily="Inter" fontSize="0.85rem" color={theme.colors.neutral['400']} mt={1}>
                                {'No connected apps yet'}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </ClickAwayListener>
        </Popper>
    </>);
}

export default ConnectedApps;
